import { useEffect, useMemo, useRef, useState } from 'react'
import { useAppDispatch } from '@/utils/typeHelpers'
import { useReselector } from '@/utils/sharedHooks'
import {
  FETCH_FINANCIAL_ACCOUNT_KEY,
  fetchFinancialAccountsIfNeeded,
} from '@/actions/financialAccountActions'
import { fetchPlaidItemsIfNeeded } from '@/actions/plaidItemActions'
import {
  FETCH_PLAID_ACCESS_TOKEN_KEY,
  PlaidUpdateStatus,
  TransactionSyncStatus,
  type PlaidItemSyncStatus,
} from '@/actions/plaid/authActions'
import {
  selectFinancialAccounts,
  selectPlaidTarget,
} from '@/selectors/financeSelectors'
import { setPlaidTarget } from '@/reducers/finances/plaidLauncherReducer'
import { useSyncStatus } from '@/components/plaid/helpers'
import { resolveSyncErrorClassification } from '@/features/user/settings/sections/self-service/financial-institutions/bankAccounts.helpers'
import {
  getConnectedInstitutions,
  type ConnectedInstitution,
} from '@/features/user/settings/sections/self-service/financial-institutions/connectNewAccountMenu.helpers'
import type { FinancialAccount } from '@/reducers/finances/financialAccountsReducer'
import { getIsFetching, selectFetchSuccess } from '@/reducers/fetch'
import type { WidgetDisplay } from '../lib/types'

/** Two surfaces can poll at once, so the card stops once a read should have finished. */
const MAX_POLL_MS = 5 * 60 * 1000

/** How long a Continue tap waits for a just-connected account to reach the store. */
const HOLD_MS = 10 * 1000

const BASELINE_KEY_PREFIX = 'connect-accounts-baseline:'

export const NO_NEW_ACCOUNTS_OUTCOME_PREFIX = 'No new accounts connected.'
export const NO_NEW_ACCOUNTS_LABEL = 'No new accounts added'

export interface ConnectedAccount {
  id: number
  /** The logo comes from the Plaid item, so the card needs its id to draw one. */
  plaidItemId: number | null
  institution: string
  name: string
  type: string
  mask: string | null
  /** The bank's count, not the account's: see the spec's open question. */
  transactionCount: number
  /** The bank has finished its read, so the row earns a check. */
  pulled: boolean
}

export interface ConnectAccountsCard {
  accounts: ConnectedAccount[]
  /** One per connected bank, for reopening Plaid on that item rather than a second one. */
  institutions: ConnectedInstitution[]
  transactionCount: number
  /** Any bank still reading, so the card says Pulling rather than a count. */
  syncing: boolean
  openPlaid: () => void
  manageBank: (accountId: number) => void
  confirm: () => void
  /** A Continue tap is waiting for a just-connected account to arrive. */
  holding: boolean
}

/** NOT_READY: the first sync found nothing yet, and the history comes by webhook. */
const isReading = (item: PlaidItemSyncStatus | undefined): boolean => {
  const status = item?.transactionSyncStatus
  if (status === TransactionSyncStatus.CONNECTING) return true
  if (status === TransactionSyncStatus.SYNCING) return true

  return (
    status === TransactionSyncStatus.IDLE &&
    item?.plaidUpdateStatus === PlaidUpdateStatus.NOT_READY
  )
}

/** Accounts connected through Plaid that are active, visible and not marked personal. Manual accounts stay off the card. */
const isConnected = (account: FinancialAccount): boolean =>
  account.plaidItemId !== null &&
  !account.inactive &&
  !account.isHidden &&
  account.markedPersonalAt === null

const toSentence = (names: string[]): string => {
  if (names.length === 1) return names[0]

  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
}

/** The accounts this card started with, or null when it never saved them. */
const readBaseline = (toolCallId: string | undefined): number[] | null => {
  if (!toolCallId) return null
  try {
    const raw = window.localStorage.getItem(
      `${BASELINE_KEY_PREFIX}${toolCallId}`
    )
    if (raw === null) return null
    const parsed: unknown = JSON.parse(raw)
    return Array.isArray(parsed) &&
      parsed.every((id): id is number => typeof id === 'number')
      ? parsed
      : null
  } catch {
    return null
  }
}

// First write wins: a remount, a reload or a second Plaid open must not move it.
const saveBaselineOnce = (toolCallId: string, ids: number[]): void => {
  try {
    const key = `${BASELINE_KEY_PREFIX}${toolCallId}`
    if (window.localStorage.getItem(key) === null)
      window.localStorage.setItem(key, JSON.stringify(ids))
  } catch {
    // Without storage the card cannot tell, so Continue lists every account.
  }
}

/**
 * What the connect card shows: the accounts Heard can see, how many transactions
 * have arrived, and the two things the card can do.
 */
export const useConnectAccounts = (
  onOutcome: (text: string, display?: WidgetDisplay) => void,
  resolved: boolean,
  toolCallId?: string
): ConnectAccountsCard => {
  const dispatch = useAppDispatch()
  const accountsById = useReselector(selectFinancialAccounts)
  const plaidTarget = useReselector(selectPlaidTarget)
  const accountsLoaded = useReselector(
    selectFetchSuccess,
    FETCH_FINANCIAL_ACCOUNT_KEY
  )
  const exchangingToken = useReselector(
    getIsFetching,
    FETCH_PLAID_ACCESS_TOKEN_KEY
  )
  const accountsFetching = useReselector(
    getIsFetching,
    FETCH_FINANCIAL_ACCOUNT_KEY
  )

  const { plaidItems, refetch } = useSyncStatus({
    enabled: !resolved,
    maxActiveDurationMs: MAX_POLL_MS,
    accountCounts: true,
    waitForHistory: true,
  })

  useEffect(() => {
    dispatch(fetchFinancialAccountsIfNeeded())
    dispatch(fetchPlaidItemsIfNeeded())
  }, [dispatch])

  // The launcher holds the target while Plaid is open and clears it on close, so
  // losing a target we saw is the only signal the browser gets that Plaid is done.
  // This only restarts the poll: the account rows are written after Plaid closes,
  // so reading them here would land before they exist.
  const wasOpen = useRef(false)
  useEffect(() => {
    if (plaidTarget) {
      wasOpen.current = true
      return
    }
    if (!wasOpen.current) return

    wasOpen.current = false
    void refetch()
  }, [plaidTarget, refetch])

  // The poll sees a new account before the store does, so it is the trigger for
  // reading the accounts again rather than the moment Plaid closed.
  useEffect(() => {
    const polled = plaidItems.flatMap((item) =>
      item.accounts.map((account) => account.id)
    )
    if (polled.some((id) => !accountsById[id])) {
      dispatch(fetchFinancialAccountsIfNeeded(true))
    }
  }, [plaidItems, accountsById, dispatch])

  const itemByAccountId = useMemo(() => {
    const byAccount = new Map<number, (typeof plaidItems)[number]>()
    for (const item of plaidItems) {
      for (const account of item.accounts) byAccount.set(account.id, item)
    }
    return byAccount
  }, [plaidItems])

  // The item's transactionsSyncedCount belongs to the bank, so it would print the
  // same total against every account on it. This is the per-account answer, which
  // only arrives because the card asks for accountCounts.
  const countByAccountId = useMemo(() => {
    const counts = new Map<number, number>()
    for (const item of plaidItems) {
      for (const account of item.accounts) {
        counts.set(account.id, account.transactionCount ?? 0)
      }
    }
    return counts
  }, [plaidItems])

  const accounts = useMemo(
    () =>
      Object.values(accountsById)
        .filter(isConnected)
        .map((account) => ({
          id: account.id,
          plaidItemId: account.plaidItemId,
          institution: account.plaidInstitutionName ?? account.name,
          name: account.name,
          type: account.subtype,
          mask: account.mask,
          transactionCount: countByAccountId.get(account.id) ?? 0,
          pulled: !isReading(itemByAccountId.get(account.id)),
        })),
    [accountsById, countByAccountId, itemByAccountId]
  )

  // A list saved after Plaid opens could already hold the new account.
  const connectStarted = useRef(false)
  useEffect(() => {
    if (resolved || !accountsLoaded || !toolCallId || connectStarted.current)
      return
    saveBaselineOnce(
      toolCallId,
      accounts.map((account) => account.id)
    )
  }, [resolved, accountsLoaded, toolCallId, accounts])

  // Plaid closes before its token exchange creates the account, so the poll alone misses it.
  const arriving =
    Boolean(plaidTarget) ||
    exchangingToken ||
    accountsFetching ||
    plaidItems.some((item) =>
      item.accounts.some((account) => !accountsById[account.id])
    )

  // Summed from the rows, so the subtitle and the list always agree.
  const transactionCount = accounts.reduce(
    (total, account) => total + account.transactionCount,
    0
  )

  const syncing = plaidItems.some(isReading)

  const institutions = useMemo(
    () =>
      getConnectedInstitutions(
        Object.values(accountsById).filter(isConnected),
        plaidItems.map((item) => ({
          ...item,
          resolvedErrorClassification: resolveSyncErrorClassification(item),
        }))
      ),
    [accountsById, plaidItems]
  )

  const openPlaid = (): void => {
    connectStarted.current = true
    dispatch(setPlaidTarget({ mode: 'connect', surface: 'adviser' }))
  }

  /** Update mode on the bank's existing item: the only way to add an account there. */
  const manageBank = (accountId: number): void => {
    connectStarted.current = true
    dispatch(setPlaidTarget({ mode: 'manage', accountId, surface: 'adviser' }))
  }

  const report = (listEveryAccount: boolean): void => {
    const names = accounts.map((account) => account.name)
    if (names.length === 0) return

    const count = `${names.length} account${names.length === 1 ? '' : 's'}`
    const list = `I have ${count} connected: ${toSentence(names)}.`
    const baseline = listEveryAccount ? null : readBaseline(toolCallId)
    if (
      baseline &&
      accounts.every((account) => baseline.includes(account.id))
    ) {
      onOutcome(`${NO_NEW_ACCOUNTS_OUTCOME_PREFIX} ${list}`, {
        label: NO_NEW_ACCOUNTS_LABEL,
      })
      return
    }
    onOutcome(list)
  }

  const [holding, setHolding] = useState(false)
  const reportRef = useRef(report)
  reportRef.current = report

  useEffect(() => {
    if (!holding) return undefined
    if (!arriving) {
      setHolding(false)
      reportRef.current(false)
      return undefined
    }
    const timer = setTimeout(() => {
      setHolding(false)
      reportRef.current(true)
    }, HOLD_MS)
    return () => clearTimeout(timer)
  }, [holding, arriving])

  const confirm = (): void => {
    if (holding) return
    if (arriving) {
      setHolding(true)
      return
    }
    report(false)
  }

  return {
    accounts,
    institutions,
    transactionCount,
    syncing,
    openPlaid,
    manageBank,
    confirm,
    holding,
  }
}
